import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Input, Button, Spin, Alert, Space } from 'antd';
import { RobotOutlined, SendOutlined, BulbOutlined } from '@ant-design/icons';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';

const { TextArea } = Input;

const AIAssistant: React.FC = () => {
  const { token } = useAuth();
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Array<{query: string, response: string, timestamp: Date}>>([]);

  const quickQuestions = [
    "What's my total revenue?",
    "How many customers do I have?",
    "What are my top services?",
    "Show me today's bookings",
    "What's my customer lifetime value?"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || !token) return;

    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/ai/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query })
      });

      if (!res.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await res.json();
      setResponse(data.reply);

      // Add to history
      setHistory(prev => [{
        query: query.trim(),
        response: data.reply,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]); // Keep last 10

    } catch (error) {
      setResponse('❌ Sorry, I couldn\'t process your request. Please try again.');
      console.error('AI Assistant error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickQuestion = (question: string) => {
    setQuery(question);
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <Card
          style={{
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none'
          }}
        >
          <div style={{ textAlign: 'center', color: 'white' }}>
            <RobotOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700 }}>
              🤖 AI Business Assistant
            </h1>
            <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
              Ask me anything about your business data and operations
            </p>
          </div>
        </Card>

        {/* Quick Questions */}
        <Card title={<><BulbOutlined /> Quick Questions</>} style={{ marginBottom: '24px' }}>
          <Space wrap style={{ width: '100%' }}>
            {quickQuestions.map((question, index) => (
              <Button
                key={index}
                type="default"
                size="small"
                onClick={() => handleQuickQuestion(question)}
                style={{ margin: '4px' }}
              >
                {question}
              </Button>
            ))}
          </Space>
        </Card>

        {/* Chat Interface */}
        <Card title="💬 Ask Your Assistant" style={{ marginBottom: '24px' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <TextArea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask me anything about your business..."
                autoSize={{ minRows: 2, maxRows: 4 }}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SendOutlined />}
                size="large"
              >
                Ask
              </Button>
            </div>
          </form>

          {/* Response */}
          {response && (
            <Alert
              message="AI Response"
              description={response}
              type="info"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '12px', color: '#666' }}>
                🤔 Analyzing your business data...
              </p>
            </div>
          )}
        </Card>

        {/* History */}
        {history.length > 0 && (
          <Card title="📚 Recent Conversations">
            {history.map((item, index) => (
              <div key={index} style={{
                padding: '12px',
                marginBottom: '8px',
                background: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>You:</strong> {item.query}
                </div>
                <div>
                  <strong>Assistant:</strong> {item.response}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#999',
                  marginTop: '4px',
                  textAlign: 'right'
                }}>
                  {item.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
